// app/src/lib/procurement/orderGuide.js — par list management (US-2.10).
//
// Average weekly usage is computed from the last 4 weeks of line items linked
// to the item. Suggested order quantity is (par − on-hand), rounded up to a
// vendor's pack size where one is available, then floored against the vendor's
// minimum order if set.

import { db } from '../db.js';
import { requireUserId, isoToday } from './util.js';
import { latestFor } from './priceBook.js';
import { mutate } from '../scale/instrument.js';

const USAGE_WEEKS = 4;

/** @typedef {{ parItemId:string, userId:string, itemId:string, parQuantity:number, uom:string, preferredVendorId?:string, reorderPoint:number, onHandQuantity?:number, lastCountDate?:string, averageWeeklyUsage?:number }} ParItem */

export async function upsert(input) {
  const userId = await requireUserId();
  if (!input.itemId) throw new Error('par.upsert: itemId required');
  const existing = await db.parItems
    .where('[userId+itemId]').equals([userId, input.itemId])
    .first();

  /** @type {ParItem} */
  const next = {
    parItemId: existing?.parItemId || input.parItemId || crypto.randomUUID(),
    userId,
    itemId: input.itemId,
    parQuantity: Number(input.parQuantity ?? existing?.parQuantity ?? 0),
    uom: input.uom || existing?.uom || 'each',
    preferredVendorId: input.preferredVendorId ?? existing?.preferredVendorId,
    reorderPoint: Number(input.reorderPoint ?? existing?.reorderPoint ?? 0),
    onHandQuantity: input.onHandQuantity != null ? Number(input.onHandQuantity) : existing?.onHandQuantity,
    lastCountDate: input.onHandQuantity != null ? (input.lastCountDate || isoToday()) : existing?.lastCountDate,
    averageWeeklyUsage: existing?.averageWeeklyUsage,
  };
  if (next.parQuantity < 0 || next.reorderPoint < 0) throw new Error('par: negative quantities not allowed');
  return mutate(
    {
      entityType: 'parItem',
      entityId: next.parItemId,
      action: existing ? 'update' : 'create',
      before: existing || undefined,
      after: next,
    },
    async () => { await db.parItems.put(next); return next; },
  );
}

export async function setOnHand(itemId, onHandQuantity) {
  const userId = await requireUserId();
  const existing = await db.parItems.where('[userId+itemId]').equals([userId, itemId]).first();
  if (!existing) throw new Error('par.setOnHand: par not configured for item');
  existing.onHandQuantity = Number(onHandQuantity);
  existing.lastCountDate = isoToday();
  await db.parItems.put(existing);
  return existing;
}

/**
 * Compute and persist average weekly usage for an item from the last 4 weeks of
 * confirmed line items (CONFIRMED or PAID_LOCAL invoices count).
 */
export async function recomputeUsage(itemId) {
  const userId = await requireUserId();
  const cutoff = new Date(); cutoff.setUTCDate(cutoff.getUTCDate() - USAGE_WEEKS * 7);
  const cutoffIso = cutoff.toISOString().slice(0, 10);

  const lineItems = await db.lineItems.where('itemId').equals(itemId).filter((li) => li.userId === userId).toArray();
  // Look up parent invoices in window.
  const invoiceIds = Array.from(new Set(lineItems.map((li) => li.invoiceId)));
  const invoices = await db.invoices.where('invoiceId').anyOf(invoiceIds).toArray();
  const invoiceById = new Map(invoices.map((i) => [i.invoiceId, i]));

  let totalQty = 0;
  for (const li of lineItems) {
    const inv = invoiceById.get(li.invoiceId);
    if (!inv) continue;
    if (inv.issuedDate < cutoffIso) continue;
    if (inv.status === 'DISPUTED') continue;
    totalQty += Number(li.quantity) || 0;
  }

  const avgPerWeek = totalQty / USAGE_WEEKS;
  const par = await db.parItems.where('[userId+itemId]').equals([userId, itemId]).first();
  if (par) {
    par.averageWeeklyUsage = avgPerWeek;
    await db.parItems.put(par);
  }
  return avgPerWeek;
}

/**
 * Build the sorted Order Guide rows: below par first, then near, then at/above.
 * Returns rows with derived display fields and the suggested order quantity.
 */
export async function listGuide() {
  const userId = await requireUserId();
  const pars = await db.parItems.where('userId').equals(userId).toArray();
  const rows = await Promise.all(pars.map(async (par) => {
    const item = await db.items.get(par.itemId);
    if (!item || item.isActive !== 1) return null;
    const onHand = Number(par.onHandQuantity ?? 0);
    const status = onHand < par.reorderPoint ? 'below' : onHand <= par.parQuantity ? 'near' : 'at-or-above';
    const suggested = Math.max(0, par.parQuantity - onHand);
    const preferredVendor = par.preferredVendorId ? await db.vendors.get(par.preferredVendorId) : null;
    // Pull the latest price for the preferred vendor so the UI can show a per-row total.
    const latestPrice = preferredVendor ? await latestFor(preferredVendor.vendorId, par.itemId) : null;
    return {
      parItemId: par.parItemId,
      itemId: par.itemId,
      itemName: item.name,
      category: item.category,
      parQuantity: par.parQuantity,
      reorderPoint: par.reorderPoint,
      onHandQuantity: onHand,
      uom: par.uom,
      averageWeeklyUsage: par.averageWeeklyUsage,
      status,
      suggestedQuantity: suggested,
      preferredVendor: preferredVendor ? { vendorId: preferredVendor.vendorId, name: preferredVendor.name } : null,
      lastUnitPriceCents: latestPrice?.priceCents,
      lastPackSize: latestPrice?.packSize,
    };
  }));
  const filtered = rows.filter(Boolean);
  const rank = { below: 0, near: 1, 'at-or-above': 2 };
  filtered.sort((a, b) => rank[a.status] - rank[b.status] || a.itemName.localeCompare(b.itemName));
  return filtered;
}

/** Count of items currently below par — used by Today "Heads up". */
export async function belowParCount() {
  const guide = await listGuide();
  return guide.filter((r) => r.status === 'below').length;
}
